/**
 * BusinessInquiriesTile — athlete-facing dashboard card that shows
 * how many brands / recruiters / fans tapped their public "Business
 * inquiries" pill. Read-only summary + a "See details" drawer with
 * the last 10 events.
 *
 * Renders NOTHING (returns null) when the athlete hasn't set a
 * `business_email` yet — no point showing "0 brands reached out"
 * to an athlete who hasn't published the pill.
 *
 * Backed by GET /api/me/athlete/business-inquiries (auth-gated).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Briefcase, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const min = Math.floor(diffMs / 60_000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

export default function BusinessInquiriesTile() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/me/athlete/business-inquiries`, { withCredentials: true });
        if (!cancelled) setData(r.data);
      } catch {
        /* silent — athlete profile probably not linked yet */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Show a soft empty state until the athlete sets a business_email
  // (otherwise the tile just reads "0 brands reached out" forever
  // and feels broken).
  if (!loaded) return null;
  if (!data) return null;
  if (!data.business_email_set) {
    return (
      <div
        className="rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-4"
        data-testid="business-inquiries-tile-empty"
      >
        <div className="flex items-center gap-2 mb-2">
          <div
            className="w-8 h-8 rounded-lg inline-flex items-center justify-center"
            style={{ background: 'rgba(10,132,255,0.15)' }}
          >
            <Briefcase className="w-4 h-4 text-blue-400" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.24em] font-black text-white/60">Business inquiries</p>
        </div>
        <p className="text-sm text-white/70 leading-relaxed">
          Add a business inquiries email in Edit Profile to surface a blue
          <span className="mx-1 inline-block align-middle px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{background:'#0A84FF', color:'white'}}>Business inquiries</span>
          pill on your profile — and start tracking who taps it.
        </p>
      </div>
    );
  }

  const month = data.this_month || 0;
  const week = data.this_week || 0;
  const total = data.total || 0;
  const recent = data.recent || [];

  return (
    <div
      className="rounded-2xl ring-1 ring-blue-500/25 bg-gradient-to-br from-blue-500/10 to-transparent p-4"
      data-testid="business-inquiries-tile"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0"
            style={{ background: '#0A84FF' }}
          >
            <Briefcase className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.24em] font-black text-blue-300">Business inquiries</p>
            <p className="text-sm font-bold text-white leading-tight">This month · {month} tap{month === 1 ? '' : 's'}</p>
          </div>
        </div>
        <p className="text-[10px] uppercase tracking-widest text-white/40 shrink-0 pt-2">
          All-time · {total}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-3">
          <p className="font-display text-2xl text-white leading-none">{month}</p>
          <p className="text-[10px] uppercase tracking-widest text-white/50 mt-1">this month</p>
        </div>
        <div className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-3">
          <p className="font-display text-2xl text-white leading-none">{week}</p>
          <p className="text-[10px] uppercase tracking-widest text-white/50 mt-1">last 7 days</p>
        </div>
      </div>

      {recent.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-white/[0.05] hover:bg-white/[0.10] ring-1 ring-white/10 text-[11px] uppercase tracking-widest font-bold text-white/70 transition"
          data-testid="business-inquiries-toggle"
        >
          {expanded ? (
            <>Hide activity <ChevronUp className="w-3 h-3" /></>
          ) : (
            <>Recent activity <ChevronDown className="w-3 h-3" /></>
          )}
        </button>
      )}

      {expanded && recent.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="business-inquiries-recent">
          {recent.map((r, i) => (
            <li
              key={i}
              className="flex items-center justify-between gap-3 text-[12px] text-white/80 rounded-lg bg-black/30 ring-1 ring-white/5 px-3 py-2"
            >
              <span className="truncate">
                {r.viewer_name || 'Anonymous visitor'}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-white/40 shrink-0">
                {fmtWhen(r.at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
